import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listSchoolsSummary, type SchoolSummary } from '../api/schools';
import { useAuth } from './AuthContext';

/**
 * Détermine le périmètre de données d'un utilisateur cap/ae.
 *
 * - `isScopedRole` : vrai pour les rôles cap/ae (accès restreint).
 * - `scopedSchoolIds` :
 *    - `null` => aucun filtrage (admin/drh voient tout) ;
 *    - `Set<string>` => seules ces écoles (celles dont l'utilisateur est responsable).
 */
export function useScopedSchoolIds(): {
  isScopedRole: boolean;
  scopedSchoolIds: Set<string> | null;
  schools: SchoolSummary[] | undefined;
} {
  const { role, userId } = useAuth();
  const isScopedRole = role === 'cap' || role === 'ae';

  const { data: schools } = useQuery<SchoolSummary[]>({
    queryKey: ['schools-summary'],
    queryFn: listSchoolsSummary,
    enabled: isScopedRole,
  });

  const scopedSchoolIds = useMemo(() => {
    if (!isScopedRole) return null;
    const set = new Set<string>();
    if (userId) {
      (schools ?? []).forEach((s) => {
        if ((s.responsibleIds ?? []).includes(userId)) {
          set.add(s.id);
        }
      });
    }
    return set;
  }, [isScopedRole, userId, schools]);

  return { isScopedRole, scopedSchoolIds, schools };
}
